import React, {Fragment, useEffect, useState, useContext} from 'react';
import {Table, Typography, Tag, Button, Select, Tooltip, Popconfirm, Row, Col} from 'antd';
import {MasterSchemaView} from './master-schema-view';
import {OpenAPIRef} from './openapi-ref';
import {DeleteOutlined} from '@ant-design/icons';
import {AddSchemaRefDialog} from './add-schema-ref-dialog';
import {AppContext} from '../state';
import {OpenApiContext} from '../openapi-context';
import {useRouteMatch} from 'react-router-dom';

export function CombinedSchemasView({schemas, combineOperator, editMode, onChange, contextPath, offset = 0}) {
    const {backend} = useContext(AppContext);
    const openapiContext = useContext(OpenApiContext);
    const {params} = useRouteMatch('/:env/standards/:category/:key/:version/:view/:element');
    const [schemaList, setSchemaList] = useState([]);
    const [operator, setOperator] = useState();

    const makeContextPath = (child) => (contextPath ? `${contextPath}.${combineOperator}[${child.key}]` : `${combineOperator}[${child.key}]`);

    const expandableConfig = {
        expandedRowRender: (record) => <MasterSchemaView schema={record.schema} editMode={editMode} contextPath={makeContextPath(record)} onChange={onChange} />,
        expandRowByClick: true,
        indentSize: 15,
        rowExpandable: (record) => (!editMode && !record.notFound) || (editMode && !record.$ref && !record.notFound),
    };

    useEffect(() => {
        if (schemas) {
            async function buildSchemaList() {
                const builtSchemas = await Promise.all(
                    schemas.map(async (schema, idx) => {
                        const s = {
                            key: `${idx}`,
                            type: schema.type || 'object',
                            schema,
                            readOnly: false,
                        };

                        if (schema.$ref) {
                            const _$ref = schema.$ref;
                            schema = await openapiContext.state.lookup(schema.$ref, params);
                            if (schema) {
                                s.$ref = _$ref;
                                s.readOnly = schema.$ref ? !editMode : false;
                            } else {
                                s.notFound = _$ref;
                                s.readOnly = true;
                            }
                        } else if (schema.type === 'array' && schema.items.$ref) {
                            s.readOnly = true;
                        } else {
                            if (schema.type === 'object' || !!schema.properties) {
                                s.name = schema.title || `object (${Object.keys(schema.properties || {}).length})`;
                            } else {
                                s.name = schema.title || `inline schema [${idx}]`;
                            }
                        }
                        return s;
                    })
                );
                setSchemaList(builtSchemas);
            }

            buildSchemaList();
        }
    }, [schemas]);

    useEffect(() => {
        setOperator(combineOperator);
    }, [combineOperator]);

    function handleOperatorChange(val) {
        onChange({op: 'change-combine-operator', old: operator, operator: val, schemaPath: contextPath});
    }

    const handleRefRemove = (record) => {
        return function () {
            onChange({op: 'rm-schema-ref', link: record.$ref, key: record.key, operator, schemaPath: contextPath});
        };
    };

    const addRef = (link) => {
        onChange({op: 'add-schema-ref', link, operator, schemaPath: contextPath});
    };

    const addInlineSchema = () => {
        onChange({op: 'add-inline-schema', schemaPath: contextPath, schema: {type: 'object', properties: {}}, operator});
    };

    const header = () => {
        switch (operator) {
            case 'allOf':
                return (
                    <span>
                        Must match <strong>all of</strong> these schemas
                    </span>
                );
            case 'anyOf':
                return (
                    <span>
                        Must match <strong>any of</strong> these schemas
                    </span>
                );
            case 'oneOf':
                return (
                    <span>
                        Must match <strong>one of</strong> these schemas
                    </span>
                );
            default:
                return <span></span>;
        }
    };

    const editHeader = () => {
        return (
            <span>
                Must match{' '}
                <Select value={operator} onChange={handleOperatorChange}>
                    <Select.Option value="allOf">All Of</Select.Option>
                    <Select.Option value="anyOf">Any Of</Select.Option>
                    <Select.Option value="oneOf">One Of</Select.Option>
                </Select>{' '}
                these schemas. You can also <AddSchemaRefDialog backend={backend} onChange={addRef} /> or{' '}
                <Button size="small" className="addRef" onClick={addInlineSchema} style={{marginTop: '5px'}}>
                    add an additional inline schema
                </Button>
            </span>
        );
    };

    // Empty arrays are treated the same as no array at all — avoids the
    // "Must match all of" header sitting above a "No data" placeholder
    // when a previous delete emptied the compose array without unsetting
    // the key (possible on specs authored before the reducer cleanup).
    return schemas && schemas.length > 0 ? (
        <Fragment>
            <Typography.Paragraph>{editMode ? editHeader() : header()}</Typography.Paragraph>
            <Row>
                <Col xs={24 - offset} offset={offset}>
                    <Table size="small" className="combinedSchemaView" expandable={expandableConfig} showHeader={false} dataSource={schemaList} pagination={false}>
                        <Table.Column
                            title="Name"
                            key="name"
                            dataIndex="name"
                            render={(name, record) => {
                                if (record.notFound) {
                                    return <Tag color="red">INVALID REFERENCE: {record.notFound}</Tag>;
                                } else {
                                    return (
                                        <span>
                                            {name} <OpenAPIRef link={record.$ref} />
                                        </span>
                                    );
                                }
                            }}
                        />
                        {editMode && (
                            <Fragment>
                                <Table.Column key="readOnly" dataIndex="readOnly" render={(readOnly) => (readOnly ? <Tag color="gold">read-only</Tag> : null)} />
                                <Table.Column
                                    key="action"
                                    render={(_, record) => (
                                        <Popconfirm
                                            title={`Do you really want to remove this ${record.schema && record.schema.properties ? 'inline schema' : 'schema $ref'}?`}
                                            okType="default"
                                            onConfirm={handleRefRemove(record)}>
                                            <Tooltip title={`Remove this ${record.schema && record.schema.properties ? 'inline schema' : 'schema reference'}`}>
                                                <Button className="deleteIcon" type="danger" size="small" icon={<DeleteOutlined />} />
                                            </Tooltip>
                                        </Popconfirm>
                                    )}
                                />
                            </Fragment>
                        )}
                    </Table>
                </Col>
            </Row>
        </Fragment>
    ) : null;
}
